import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Editor } from '@monaco-editor/react';
import { workflowService } from '../../services/workflowService';
import { useNotificationStore } from '../../store/notificationStore';
import type { CreateWorkflowRequest, UpdateWorkflowRequest } from '../../services/workflowService';

interface WorkflowDesignerProps {
  workflowId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

/**
 * Workflow designer component with JSON editor.
 * Note: A visual drag-and-drop designer using react-flow can be added later.
 */
export const WorkflowDesigner = ({ workflowId, onSave, onCancel }: WorkflowDesignerProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(!!workflowId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowJson, setWorkflowJson] = useState('{\n  "nodes": [],\n  "edges": []\n}');
  const [workflowType, setWorkflowType] = useState('Custom');
  const [patternType, setPatternType] = useState('None');
  const [supportsCheckpointing, setSupportsCheckpointing] = useState(true);
  const [supportsStreaming, setSupportsStreaming] = useState(false);
  const [version, setVersion] = useState('1.0.0');

  useEffect(() => {
    if (workflowId) {
      loadWorkflow();
    }
  }, [workflowId]);

  const loadWorkflow = async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const workflow = await workflowService.getWorkflow(workflowId);
      setName(workflow.name);
      setDescription(workflow.description || '');
      setWorkflowJson(workflow.workflowJson);
      setWorkflowType(workflow.workflowType);
      setPatternType(workflow.patternType);
      setSupportsCheckpointing(workflow.supportsCheckpointing);
      setSupportsStreaming(workflow.supportsStreaming);
      setVersion(workflow.version);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Workflow name is required');
      return;
    }

    try {
      // Validate JSON
      JSON.parse(workflowJson);
    } catch {
      setError('Invalid JSON format');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (workflowId) {
        const updateRequest: UpdateWorkflowRequest = {
          name,
          description,
          workflowJson,
          isActive: true,
          version,
        };
        await workflowService.updateWorkflow(workflowId, updateRequest);
        addNotification('Workflow updated successfully', 'success');
      } else {
        const createRequest: CreateWorkflowRequest = {
          name,
          description,
          workflowJson,
          workflowType: workflowType as any,
          patternType: patternType as any,
          supportsCheckpointing,
          supportsStreaming,
          version,
        };
        await workflowService.createWorkflow(createRequest);
        addNotification('Workflow created successfully', 'success');
      }
      onSave?.();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {workflowId ? 'Edit Workflow' : 'Create Workflow'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {onCancel && (
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save Workflow'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Basic Info" />
          <Tab label="Workflow Definition" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Workflow Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Workflow Type</InputLabel>
                <Select value={workflowType} onChange={(e) => setWorkflowType(e.target.value)} label="Workflow Type">
                  <MenuItem value="Sequential">Sequential</MenuItem>
                  <MenuItem value="Parallel">Parallel</MenuItem>
                  <MenuItem value="Conditional">Conditional</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Pattern Type</InputLabel>
                <Select value={patternType} onChange={(e) => setPatternType(e.target.value)} label="Pattern Type">
                  <MenuItem value="None">None</MenuItem>
                  <MenuItem value="ResearchWriteReview">Research-Write-Review</MenuItem>
                  <MenuItem value="ParallelAnalysis">Parallel Analysis</MenuItem>
                  <MenuItem value="ConditionalRemediation">Conditional Remediation</MenuItem>
                  <MenuItem value="HumanInTheLoopApproval">Human-in-the-Loop</MenuItem>
                  <MenuItem value="RetryWithBackoff">Retry with Backoff</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define your workflow as JSON. The workflow should contain nodes and edges.
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
              <Editor
                height="500px"
                defaultLanguage="json"
                value={workflowJson}
                onChange={(value) => setWorkflowJson(value || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
